import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormControl, FormGroup, NgForm, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {of, ReplaySubject} from 'rxjs';
import {concatMap, map, takeUntil} from 'rxjs/operators';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {DobryAgentAuthor} from '../../../../shared/interface/dobry-agent-blog-article';
import {DobryAgentAuthorService} from '../../../../shared/services/dobry-agent-author.service';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {User} from "../../../../shared/interface/user";
import {Partner} from "../../../../shared/interface/partner";
import {Ofwca} from "../../../../shared/interface/ofwca";
import {PartnerService} from "../../../../shared/services/partner.service";
import {OfwcaService} from "../../../../shared/services/ofwca.service";
import {UserService} from "../../../../shared/services/user.service";
import {MatDialog} from "@angular/material/dialog";
import {AvatarCropDialogComponent} from "../../../user/avatar-crop-dialog/avatar-crop-dialog.component";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";

@Component({
  selector: 'app-da-author-add',
  templateUrl: './da-author-add.component.html',
  styleUrls: ['./da-author-add.component.scss']
})
export class DaAuthorAddComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() author?: DobryAgentAuthor;
  @Input() mod = false;

  @Output() closeModification = new EventEmitter<void>();
  @ViewChild('imageInput') avatarSelect: ElementRef;

  uuid?: string;
  isEditMode = false;
  loading = false;
  saving = false;

  selectedImageFile: File | null = null;
  selectedImageFileName = '';
  selectedImagePreview: string | null = null;

  authorReactiveForm: FormGroup;
  selectedAsfUser: any = null;

  asfUserControl: FormControl = new FormControl(null);

  user24Id: string | null = null;
  userObject: User | null = null;

  partnerId: string | null = null;
  partnerObject: Partner | null = null;

  ofwcaId: string | null = null;
  ofwcaObject: Ofwca | null = null;

  deletingImage = false;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis autora...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      []
    ]
  };

  model: Partial<DobryAgentAuthor> = {
    firstName: '',
    lastName: '',
    altImage: '',
    active: true,
  };

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private authorService: DobryAgentAuthorService,
    private userService: UserService,
    private partnerService: PartnerService,
    private ofwcaService: OfwcaService,
    private fb: FormBuilder,
  ) {
    this.authorReactiveForm = this.fb.group({
      description: ['', Validators.required],
    });
  }


  ngOnInit(): void {
    if (this.mod && this.author) {
      this.setModel(this.author);
      this.configureRequiredFields();
      return;
    }

    this.isEditMode = false;
    this.configureRequiredFields();
  }
  getUserControl(): FormControl {
    return this.asfUserControl;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.author && this.author) {
      this.setModel(this.author);
    }
  }

  ngOnDestroy(): void {
    this.clearSelectedImagePreview();

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get hasAuthorImage(): boolean {
    return !!this.selectedImageFile || !!this.model.imagePath;
  }

  private configureRequiredFields(): void {
    if (!this.isEditMode) {
      this.asfUserControl.setValidators([
        Validators.required
      ]);
    } else {
      this.asfUserControl.clearValidators();
    }

    this.asfUserControl.updateValueAndValidity({
      emitEvent: false
    });
  }

  save(form: NgForm, addNext = false): void {
    if (
      form.invalid
      || this.authorReactiveForm.invalid
      || this.asfUserControl.invalid
      || this.saving
    ) {
      form.control.markAllAsTouched();
      this.authorReactiveForm.markAllAsTouched();
      this.asfUserControl.markAsTouched();

      return;
    }

    this.saving = true;

    const formData = {
      firstName: this.model.firstName,
      lastName: this.model.lastName,
      description: this.authorReactiveForm.get('description')?.value,
      altImage: this.model.altImage,
      active: !!this.model.active,

      asfUserUuid: this.selectedAsfUser?.uuid ?? this.model.asfUserUuid ?? null,
      asfOfwcaUuid: this.selectedAsfUser?.ofwcaId ?? this.model.asfOfwcaUuid ?? null,
      asfPartnerUuid: this.selectedAsfUser?.partnerId ?? this.model.asfPartnerUuid ?? null,
      asfEmail: this.selectedAsfUser?.email ?? this.model.email ?? null,
      asfPhone: this.selectedAsfUser?.phone ?? this.model.phone ?? null,
    };

    this.authorService.send(formData, this.uuid)
      .pipe(
        takeUntil(this.destroyed$),
        concatMap((result: Uuid) => {
          if (this.selectedImageFile && result?.uuid) {
            return this.authorService
              .uploadImage(this.selectedImageFile, result.uuid)
              .pipe(map(() => result));
          }

          return of(result);
        })
      )
      .subscribe({
        next: (result: Uuid) => {
          this.saving = false;

          this.snackBar.open(
            this.isEditMode ? 'Autor został zapisany.' : 'Autor został dodany.',
            'Zamknij',
            {duration: 3000}
          );

          this.selectedImageFile = null;
          this.selectedImageFileName = '';
          this.clearSelectedImagePreview();

          if (this.mod) {
            this.closeModification.emit();
            return;
          }

          if (addNext) {
            this.resetForm(form);
            return;
          }

          if (result?.uuid) {
            this.router.navigate(['/blog-dobry-agent/autorzy/szczegoly', result.uuid]);
            return;
          }

          this.router.navigate(['/blog-dobry-agent/autorzy']);
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.handleBackendError(error, form);
        }
      });
  }

  cancel(): void {
    if (this.mod) {
      this.closeModification.emit();
      return;
    }

    this.router.navigate(['/blog-dobry-agent/autorzy']);
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.snackBar.open(
        'Wybrany plik nie jest obrazkiem.',
        'Zamknij',
        {duration: 4000}
      );

      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const fileBase64 = reader.result?.toString();

      if (!fileBase64) {
        this.snackBar.open(
          'Nie udało się odczytać pliku.',
          'Zamknij',
          {duration: 3000}
        );

        input.value = '';
        return;
      }

      const dialogRef = this.dialog.open(AvatarCropDialogComponent, {
        width: '55%',
        data: {
          fileBase64,
          ratio: 1,
          roundCropper: true,
          maintainRatio: true,
          compressedFile: false,
        },
        height: '610px',
        maxHeight: '90%',
        panelClass: 'full-height-dialog',
      });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((result: File | Blob | string | null) => {
          input.value = '';

          if (!result) {
            return;
          }

          const croppedFile = this.normalizeCroppedImage(result, file);

          if (!croppedFile) {
            this.snackBar.open(
              'Nie udało się przygotować wykadrowanego zdjęcia.',
              'Zamknij',
              {duration: 3000}
            );

            return;
          }

          this.clearSelectedImagePreview();

          this.selectedImageFile = croppedFile;
          this.selectedImageFileName = croppedFile.name;

          if (typeof result === 'string') {
            this.selectedImagePreview = result.startsWith('data:')
              ? result
              : `data:image/jpeg;base64,${result}`;
            return;
          }

          this.selectedImagePreviewFromFile(croppedFile);
        });
    };

    reader.onerror = () => {
      this.snackBar.open(
        'Nie udało się odczytać pliku.',
        'Zamknij',
        {duration: 3000}
      );

      input.value = '';
    };
  }

  private selectedImagePreviewFromFile(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      this.selectedImagePreview = reader.result?.toString() ?? null;
    };

    reader.readAsDataURL(file);
  }

  private normalizeCroppedImage(result: File | Blob | string, originalFile: File): File | null {
    if (result instanceof File) {
      return result;
    }

    if (result instanceof Blob) {
      return new File(
        [result],
        this.changeFileExtension(originalFile.name, 'jpg'),
        {type: result.type || 'image/jpeg'}
      );
    }

    if (typeof result === 'string') {
      const blob = result.startsWith('data:')
        ? this.dataUrlToBlob(result)
        : this.base64ToBlob(result, 'image/jpeg');

      if (!blob) {
        return null;
      }

      return new File(
        [blob],
        this.changeFileExtension(originalFile.name, 'jpg'),
        {type: 'image/jpeg'}
      );
    }

    return null;
  }

  private base64ToBlob(base64: string, mime: string): Blob | null {
    try {
      const cleanBase64 = base64.includes(',')
        ? base64.split(',')[1]
        : base64;

      const binary = atob(cleanBase64);
      const length = binary.length;
      const bytes = new Uint8Array(length);

      for (let i = 0; i < length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      return new Blob([bytes], {type: mime});
    } catch (e) {
      return null;
    }
  }

  private changeFileExtension(fileName: string, extension: string): string {
    const cleanExtension = extension.replace('.', '');

    if (!fileName.includes('.')) {
      return `${fileName}.${cleanExtension}`;
    }

    return fileName.replace(/\.[^/.]+$/, `.${cleanExtension}`);
  }

  private dataUrlToBlob(dataUrl: string): Blob | null {
    try {
      const parts = dataUrl.split(',');

      if (parts.length !== 2) {
        return null;
      }

      const meta = parts[0];
      const base64 = parts[1];
      const mimeMatch = meta.match(/data:(.*);base64/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';

      return this.base64ToBlob(base64, mime);
    } catch (e) {
      return null;
    }
  }

  private clearSelectedImagePreview(): void {
    if (this.selectedImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedImagePreview);
    }

    this.selectedImagePreview = null;
  }

  clearSelectedImage(imageInput: HTMLInputElement): void {
    this.selectedImageFile = null;
    this.selectedImageFileName = '';
    this.clearSelectedImagePreview();
    imageInput.value = '';
  }

  clearBackendError(form: NgForm, fieldName: string): void {
    const control = form.controls[fieldName];

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  clearDescriptionBackendError(): void {
    const control = this.authorReactiveForm.get('description');

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private handleBackendError(error: HttpErrorResponse, form: NgForm): void {
    const backendError = error.error?.error;
    const errorList = backendError?.list;

    if (errorList) {
      Object.keys(errorList).forEach((fieldName: string) => {
        const fieldErrors = errorList[fieldName];

        if (fieldName === 'description' && fieldErrors?.length) {
          this.authorReactiveForm.get('description')?.setErrors({
            backend: fieldErrors[0]
          });

          this.authorReactiveForm.get('description')?.markAsTouched();
          return;
        }

        if (form.controls[fieldName] && fieldErrors?.length) {
          form.controls[fieldName].setErrors({
            backend: fieldErrors[0]
          });

          form.controls[fieldName].markAsTouched();
        }
      });

      this.snackBar.open(
        backendError?.text || 'Formularz zawiera błędy.',
        'Zamknij',
        {duration: 4000}
      );

      return;
    }

    this.snackBar.open(
      backendError?.text || 'Nie udało się zapisać autora. Sprawdź formularz i spróbuj ponownie.',
      'Zamknij',
      {duration: 4000}
    );
  }
  private setModel(author: DobryAgentAuthor): void {
    this.uuid = author.uuid;
    this.isEditMode = true;

    const user24Id = author.user24Id ?? author.agent?.user24Id ?? null;
    const ofwca24Id = author.agent?.ofwca24Id ?? null;
    const partner24Id = author.agent?.partner24Id ?? null;

    this.model = {
      uuid: author.uuid,
      firstName: author.firstName ?? '',
      lastName: author.lastName ?? '',
      altImage: author.altImage ?? '',
      imagePath: author.imagePath ?? '',
      active: author.active ?? false,

      asfUserUuid: user24Id,
      asfOfwcaUuid: ofwca24Id,
      asfPartnerUuid: partner24Id,
      email: author.agent?.email ?? author.email ?? null,
      phone: author.agent?.phone ?? author.phone ?? null,
    };

    this.authorReactiveForm.patchValue({
      description: author.description ?? '',
    });

    this.selectedAsfUser = user24Id ? {
      uuid: user24Id,
      ofwcaId: ofwca24Id,
      partnerId: partner24Id,
      email: author.agent?.email ?? author.email ?? null,
      phone: author.agent?.phone ?? author.phone ?? null,
      firstName: author.agent?.firstName ?? author.firstName ?? null,
      lastName: author.agent?.lastName ?? author.lastName ?? null,
    } : null;

    this.asfUserControl.setValue(
      this.selectedAsfUser,
      {emitEvent: false}
    );

    this.userObject = null;
    this.partnerObject = null;
    this.ofwcaObject = null;

    this.user24Id = user24Id;
    this.partnerId = partner24Id;
    this.ofwcaId = ofwca24Id;

    if (this.user24Id) {
      this.loadUser(this.user24Id);
    }

    if (this.partnerId) {
      this.loadPartner(this.partnerId);
    }

    if (this.ofwcaId) {
      this.loadOfwca(this.ofwcaId);
    }
  }
  private resetForm(form: NgForm): void {
    this.uuid = undefined;
    this.isEditMode = false;

    this.model = {
      firstName: '',
      lastName: '',
      altImage: '',
      active: true,
    };

    this.selectedImageFile = null;
    this.selectedImageFileName = '';
    this.clearSelectedImagePreview();

    form.resetForm(this.model);

    this.authorReactiveForm.reset({
      description: '',
    });
  }


  selectedUser(user: User): void {
    this.selectedAsfUser = user;

    this.asfUserControl.setValue(user);
    this.asfUserControl.markAsTouched();

    this.userObject = null;
    this.partnerObject = null;
    this.ofwcaObject = null;

    this.user24Id = user.uuid ?? null;
    this.partnerId = user.partnerId ?? null;
    this.ofwcaId = user.ofwcaId ?? null;

    this.model.firstName = user.firstName;
    this.model.lastName = user.lastName;
    this.model.altImage = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    this.model.asfUserUuid = user.uuid;
    this.model.asfOfwcaUuid = user.ofwcaId ?? null;
    this.model.asfPartnerUuid = user.partnerId ?? null;
    this.model.email = user.email ?? null;
    this.model.phone = user.phone ?? null;

    if (this.user24Id) {
      this.loadUser(this.user24Id);
    }

    if (this.partnerId) {
      this.loadPartner(this.partnerId);
    }

    if (this.ofwcaId) {
      this.loadOfwca(this.ofwcaId);
    }

    this.snackBar.open(
      'Użytkownik ASF24 został przypisany do autora.',
      'Zamknij',
      {duration: 2500}
    );
  }

  clearSelectedAsfUser(): void {
    this.selectedAsfUser = null;

    this.asfUserControl.reset();

    this.user24Id = null;
    this.userObject = null;

    this.partnerId = null;
    this.partnerObject = null;

    this.ofwcaId = null;
    this.ofwcaObject = null;

    this.model.asfUserUuid = null;
    this.model.asfOfwcaUuid = null;
    this.model.asfPartnerUuid = null;
    this.model.email = null;
    this.model.phone = null;
  }

  loadUser(user24Id: string): void {
    this.userService.get(user24Id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user: User) => {
          if (this.user24Id !== user24Id) {
            return;
          }

          this.userObject = user;
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open('Nie udało się pobrać danych użytkownika', error.status.toString(), {
            duration: 2000
          });
        }
      });
  }

  loadPartner(partnerId: string): void {
    this.partnerService.get(partnerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (partner: Partner) => {
          if (this.partnerId !== partnerId) {
            return;
          }

          this.partnerObject = partner;
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open('Nie udało się pobrać danych partnera', error.status.toString(), {
            duration: 2000
          });
        }
      });
  }

  loadOfwca(ofwcaId: string): void {
    this.ofwcaService.get(ofwcaId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (ofwca: Ofwca) => {
          if (this.ofwcaId !== ofwcaId) {
            return;
          }

          this.ofwcaObject = ofwca;
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open('Nie udało się pobrać danych OFWCA', error.status.toString(), {
            duration: 2000
          });
        }
      });
  }

  public openFileDialog(): void {
    const e: HTMLElement = this.avatarSelect.nativeElement;
    e.click();
  }

  deleteCurrentImage(): void {
    if (!this.uuid || !this.model.imagePath || this.deletingImage) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć zdjęcie autora?'
      },
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }

        this.deletingImage = true;

        this.authorService
          .deleteImage(this.uuid)
          .pipe(
            takeUntil(this.destroyed$)
          )
          .subscribe({
            next: () => {
              this.deletingImage = false;

              this.model.imagePath = null;

              this.snackBar.open(
                'Zdjęcie autora zostało usunięte.',
                'Zamknij',
                {duration: 3000}
              );
            },
            error: (error: HttpErrorResponse) => {
              this.deletingImage = false;

              this.snackBar.open(
                error.error?.message ?? 'Nie udało się usunąć zdjęcia autora.',
                'Zamknij',
                {duration: 4000}
              );
            }
          });
      });
  }
}
